/**
 * Team (account membership + invitations) DTOs.
 *
 * Mirrors Prisma `AccountMembership` + `AccountInvitation`. Business-tier
 * feature; web parity with `app/api/team/*`. Inviting a member does NOT
 * change billing (flat team-seat allowance), so it is not an in-app purchase.
 *
 * The mobile mirror at `/api/mobile/v1/account/team/*` consumes these via
 * `safeParseOrWarn`.
 */
import { z } from "zod";

export const TeamRoleSchema = z.enum([
  "OWNER",
  "ADMIN",
  "MEMBER",
  "SITE_SUPERVISOR",
]);
export type TeamRole = z.infer<typeof TeamRoleSchema>;

/** Roles a manager can assign / invite (never OWNER). */
export const TeamInviteRoleSchema = z.enum([
  "ADMIN",
  "MEMBER",
  "SITE_SUPERVISOR",
]);
export type TeamInviteRole = z.infer<typeof TeamInviteRoleSchema>;

export const TeamMemberSchema = z.object({
  id: z.string(),
  role: TeamRoleSchema,
  name: z.string().nullable(),
  email: z.string(),
  isYou: z.boolean(),
  acceptedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: TeamRoleSchema,
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type TeamInvitation = z.infer<typeof TeamInvitationSchema>;

export const TeamListResponseSchema = z.object({
  members: z.array(TeamMemberSchema),
  invitations: z.array(TeamInvitationSchema),
  /** Can the caller create/cancel at least one invitation (OWNER or ADMIN)? */
  canManage: z.boolean(),
  /** Exact invitation roles this caller may create or cancel. */
  assignableRoles: z.array(TeamInviteRoleSchema),
  /** Can the caller change roles / remove members (OWNER only)? */
  canManageMembers: z.boolean(),
  seatLimit: z.number(),
  seatsUsed: z.number(),
});
export type TeamListResponse = z.infer<typeof TeamListResponseSchema>;

// ─── #2411 server-boundary variants ─────────────────────────────────────────
// The mobile app parses the permissive bases above; the team route serialises
// through these Strict forms (full depth — members and invitations included).

export const TeamMemberStrict = TeamMemberSchema.strict();
export const TeamInvitationStrict = TeamInvitationSchema.strict();

export const TeamListResponseStrict = TeamListResponseSchema.extend({
  members: z.array(TeamMemberStrict),
  invitations: z.array(TeamInvitationStrict),
}).strict();

export const TeamInviteRequest = z
  .object({
    email: z.string().email(),
    role: TeamInviteRoleSchema.default("MEMBER"),
  })
  .strict();
export type TeamInviteRequest = z.infer<typeof TeamInviteRequest>;

export const TeamMemberRoleUpdateRequest = z
  .object({
    role: TeamInviteRoleSchema,
  })
  .strict();
export type TeamMemberRoleUpdateRequest = z.infer<
  typeof TeamMemberRoleUpdateRequest
>;
